import { OrgStatus } from '../interfaces/organization';
import { SubscriptionStatus } from '../interfaces/subscription';
export const SUBSCRIPTION_EXPIRED = 'Your subscription has expired';
export const SUBSCRIPTION_INCOMPLETE = 'Your subscription setup is incomplete';
export const SUBSCRIPTION_PAUSED = 'Your subscription is currently paused';
export const SUBSCRIPTION_PAST_DUE = 'Your subscription payment is past due';
export const SUBSCRIPTION_INACTIVE = 'Your subscription is inactive';
export const SUBSCRIPTION_INVALID = 'Unable to validate your subscription';
export const SUBSCRIPTION_NOT_FOUND = 'Unable to find your subscription';
export const SUBSCRIPTION_FREE = 'You are on the free plan';
export const SUBSCRIPTION_TRIAL = 'You are on a free trial';

/*
export const ADMIN_NOT_AVAILABLE_FOR_GUEST =
  '{role} is not available for {license} licenses';
*/

export const SUBSCRIPTION_EXPIRED_STATUS_MSG = `${SUBSCRIPTION_EXPIRED}. Please upgrade to continue!`;
export const SUBSCRIPTION_INCOMPLETE_STATUS_MSG = `${SUBSCRIPTION_INCOMPLETE}. Please complete the setup to continue.`;
export const SUBSCRIPTION_PAUSED_STATUS_MSG = `${SUBSCRIPTION_PAUSED}. Please resume your subscription to continue.`;
export const SUBSCRIPTION_PAST_DUE_STATUS_MSG = `${SUBSCRIPTION_PAST_DUE}. Please update your payment method to continue.`;
export const SUBSCRIPTION_INACTIVE_STATUS_MSG = `${SUBSCRIPTION_INACTIVE}. Please (re)activate your subscription to continue.`;
export const SUBSCRIPTION_FREE_STATUS_MSG = `${SUBSCRIPTION_FREE}. Upgrade for full access!`;

export const SUBSCRIPTION_EXPIRED_ALERT = `${SUBSCRIPTION_EXPIRED}`;
export const SUBSCRIPTION_NOT_FOUND_ALERT = `${SUBSCRIPTION_NOT_FOUND}`;
export const SUBSCRIPTION_INACTIVE_ALERT =
  'Your subscription needs to be (re)activated.';
export const SUBSCRIPTION_INCOMPLETE_ALERT = `Your subscription is not set up correctly.`;
export const SUBSCRIPTION_INVALID_ALERT = `${SUBSCRIPTION_INVALID}`;
export const SUBSCRIPTION_EXPIRING_SOON_ALERT = `Your subscription is ending{numDays}.`;
export const SUBSCRIPTION_CONVERTING_SOON_ALERT = `Your trial will convert to {plan}{numDays}.`;
export const SUBSCRIPTION_TRIAL_ENDING_SOON_ALERT = `Your free trial will end{numDays}.`;
export const SUBSCRIPTION_FREE_ALERT = `${SUBSCRIPTION_FREE}`;

export const CONTACT_YOUR_ADMINISTRATOR = `Contact your administrator for details.`;
export const NO_SALESFORCE_ORG_CONNECTED =
  'No Salesforce org connected. Please connect to a Salesforce org to continue.';
export const SALESFORCE_ORG_INVALID =
  'Salesforce org is invalid. Please switch to a valid Salesforce org.';
export const SALESFORCE_ACCESS_TOKEN_EXPIRED =
  'Salesforce authorization has expired. Please re-authorize Cirra AI.';
export const SALESFORCE_ORG_DELETED =
  'Salesforce org and/or user are probably deleted. Please contact your administrator.';
export const SALESFORCE_ORG_UNKNOWN =
  'Salesforce Organization status is unknown. You can attempt to re-authorize Cirra AI.';

/*
export const LICENSE_INFO =
  'Full licenses grant complete platform access, while Guest licenses provide read-only access.';
export const NO_VALID_ORG_LICENSE =
  "You don't have a license under your Organization account.";
export const GUEST_LICENSE =
  'You have a Guest license with limited access. Please contact your administrator to upgrade to a Full license.';
export const FULL_LICENSE = 'Full license with complete platform access.';
*/

export const NOT_FOUND = 'Item not found.';
export const PERSONAL_EMAIL_SIGNUP_ERROR =
  'Please use your work email to sign up.';

export const NOT_OWN_CHANGE_REQUEST = `This Change Request was created by another user`;

export const PROMPT_COMING_SOON =
  'This prompt template will be available in an upcoming release';

const _getSomethingHappensSoonMessage = (
  messageTemplate: string,
  numDays: number | null | undefined
): string => {
  const numDaysString =
    numDays === 1 ? ' tomorrow' : ` in ${numDays?.toString()} days` || '';
  return messageTemplate.replace('{numDays}', numDaysString);
};

export const getExpiringSoonMessage = (
  expiresIn: number | null | undefined
): string => {
  return _getSomethingHappensSoonMessage(
    SUBSCRIPTION_EXPIRING_SOON_ALERT,
    expiresIn
  );
};

export const getConvertingSoonMessage = (
  convertsIn: number | null | undefined,
  convertingToPaidPlan: boolean,
  conversionProduct: string | null
): string => {
  let plan = '';
  if (conversionProduct) {
    plan = `the ${conversionProduct}`;
  } else if (convertingToPaidPlan) {
    plan = 'a paid plan';
  } else {
    plan = 'the free plan';
  }

  const msg = _getSomethingHappensSoonMessage(
    SUBSCRIPTION_CONVERTING_SOON_ALERT,
    convertsIn
  );
  return msg.replace('{plan}', plan);
};

export const getTrialEndingSoonMessage = (
  expiresIn: number | null | undefined
): string => {
  return _getSomethingHappensSoonMessage(
    SUBSCRIPTION_TRIAL_ENDING_SOON_ALERT,
    expiresIn
  );
};
export const getSubscriptionStatusMessage = (
  status: SubscriptionStatus | undefined
): string => {
  if (!status) return '';

  switch (status) {
    case SubscriptionStatus.INCOMPLETE:
      return SUBSCRIPTION_INCOMPLETE_STATUS_MSG;
    case SubscriptionStatus.PAUSED:
      return SUBSCRIPTION_PAUSED_STATUS_MSG;
    case SubscriptionStatus.PAST_DUE:
      return SUBSCRIPTION_PAST_DUE_STATUS_MSG;
    case SubscriptionStatus.INACTIVE:
      return SUBSCRIPTION_INACTIVE_STATUS_MSG;
    case SubscriptionStatus.EXPIRED:
      return SUBSCRIPTION_EXPIRED_STATUS_MSG;
    case SubscriptionStatus.FREE:
      return SUBSCRIPTION_FREE_STATUS_MSG;
    default:
      return SUBSCRIPTION_EXPIRED_STATUS_MSG;
  }
};

export const getSFOrgStatusMessage = (
  status: OrgStatus = OrgStatus.NEW
): string => {
  switch (status) {
    case OrgStatus.NEW:
      return NO_SALESFORCE_ORG_CONNECTED;
    case OrgStatus.INVALID:
      return SALESFORCE_ORG_INVALID;
    case OrgStatus.EXPIRED:
      return SALESFORCE_ACCESS_TOKEN_EXPIRED;
    case OrgStatus.DELETED:
      return SALESFORCE_ORG_DELETED;
    case OrgStatus.UNKNOWN:
      return SALESFORCE_ORG_UNKNOWN;
    default:
      return '';
  }
};
